import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import TrainerContext from "../../../../context/trainerContext/TrainerContext";
import { isAccessAllowed } from "../../../../utils/helpers";
import makeRequest from "../../../../api";
import { assessmentService, userService } from "../../../../api/services";
import { toast } from "react-toastify";

const UpdateReportPage = () => {
  const params = useParams();
  const userId = params.userId;
  const reportId = params.reportId;
  const subscription = useLocation().state;

  const isEditReportAllowed = isAccessAllowed("REPORT_EDIT");

  const { getTrainerState } = useContext(TrainerContext);
  const trainerData = getTrainerState();

  const { trainerId, trainerName } = trainerData;

  const [healthMetrics, setHealthMetrics] = useState([]);
  const [healthMetricsMap, setHealthMetricsMap] = useState<{
    [key: string]: any;
  }>({});
  const [existingHealthMetrics, setExistingHealthMetrics] = useState<any>([]);
  const [issueTypes, setIssueTypes] = useState<any>([]);
  const [issueTypeMap, setIssueTypeMap] = useState<{ [key: string]: any }>({});
  const [fileTypeOption, setFileTypeOptions] = useState([]);
  const [inputData, setInputData] = useState<{ [key: string]: any }>({});
  const [note, setNote] = useState<{ [key: string]: any }>({});
  const [healthMetric, setHealthMetric] = useState<{ [key: string]: any }>({
    metricName: "",
    metricValue: "",
  });
  const [userDetails, setUserDetails] = useState<any>(null);

  const fetchReportDetails = async () => {
    if (!reportId || !userId) {
      return;
    }
    try {
      const res = await makeRequest(
        assessmentService.getUserReportDetails(reportId, userId)
      );
      setInputData({ ...res?.data?.body });
      if (
        res?.data?.body?.healthMetrics &&
        Array.isArray(res?.data?.body?.healthMetrics)
      ) {
        setExistingHealthMetrics([...res?.data?.body?.healthMetrics]);
      }
    } catch (error: any) {
      toast.error("Error fetching user's report details!");
    }
  };

  const fetchIssueTypes = async () => {
    try {
      const res = await makeRequest(
        assessmentService.getHealthMetricsOptions()
      );
      setIssueTypes([...res?.data?.body?.issueBasedOptions]);
    } catch (error: any) {
      toast.error("Error fetching health metrics options!");
    }
  };

  useEffect(() => {
    fetchReportDetails();
    fetchIssueTypes();
  }, []);

  const getUserData = async () => {
    try {
      const res = await makeRequest(userService.getUserDetails(userId));
      setUserDetails({ ...res?.data?.userDto });
    } catch (error: any) {
      toast.error("Error fetching user details!");
    }
  };

  useEffect(() => {
    if (userId) {
      getUserData();
    }
  }, [userId]);

  useEffect(() => {
    if (Array.isArray(healthMetrics)) {
      let healthMetricMap = {};
      healthMetrics.forEach((item: any) => {
        healthMetricMap = {
          ...healthMetricMap,
          [item.value]: item.displayName,
        };
      });
      setHealthMetricsMap({ ...healthMetricMap });
    }
  }, [healthMetrics]);

  useEffect(() => {
    if (issueTypes.length) {
      let issueMap = {};
      issueTypes?.map((item: any) => {
        issueMap = {
          ...issueMap,
          [item?.healthIssueType]: {
            options: item?.options,
            displayName: item?.displayName,
          },
        };
      });
      setIssueTypeMap({ ...issueMap });
    }
  }, [issueTypes]);

  useEffect(() => {
    if (inputData?.healthIssueType !== "") {
      const issueKey = inputData?.healthIssueType as keyof typeof issueTypeMap;
      setFileTypeOptions(issueTypeMap[issueKey]?.options?.documentType);
      setHealthMetrics(issueTypeMap[issueKey]?.options?.healthMetrics);
    }
  }, [inputData, issueTypeMap]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "reportGeneratedAt") {
      const date = new Date(value);
      setInputData({ ...inputData, [name]: moment(date).valueOf().toString() });
    } else if (name === "note") {
      setNote({
        trainerDetails: {
          trainerId: trainerId,
          trainerName: trainerName,
        },
        text: value,
        createdAt: moment().valueOf(),
      });
    } else if (name === "healthMetricValue") {
      setHealthMetric({
        ...healthMetric,
        metricValue: value,
      });
    } else if (name === "healthMetricKey") {
      setHealthMetric({
        ...healthMetric,
        metricName: value,
      });
    } else {
      setInputData({ ...inputData, [name]: value });
    }
  };

  const handleAddNote = () => {
    if (Object.keys(note).length === 0) {
      return;
    }
    setInputData({
      ...inputData,
      notes: Array.isArray(inputData?.notes)
        ? [...inputData?.notes, note]
        : [note],
    });
    setNote({});
  };

  const handleAddMetric = () => {
    if (healthMetric.metricName === "" || healthMetric.metricValue === "") {
      return;
    }
    setInputData({
      ...inputData,
      healthMetrics: Array.isArray(inputData?.healthMetrics)
        ? [...inputData?.healthMetrics, healthMetric]
        : [healthMetric],
    });
    setHealthMetric({
      metricName: "",
      metricValue: "",
    });
  };

  const handleMetricDelete = async (metricName: any) => {
    const existing = existingHealthMetrics?.find(
      (item: any) => item?.metricName === metricName
    );
    if (existing) {
      try {
        const res = await makeRequest(
          assessmentService.deleteUserHealthMetric(
            { reportId, metricName },
            userId
          )
        );
        toast.success("Health metric deleted successfully.");
        window.location.reload();
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
      }
    } else {
      setInputData({
        ...inputData,
        healthMetrics: inputData?.healthMetrics?.filter(
          (item: any) => metricName !== item.metricName
        ),
      });
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    try {
      const res = await makeRequest(
        assessmentService.updateUserReportDetails(inputData, reportId, userId)
      );
      toast.success("Report updated successfully.");
      window.location.reload();
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  const isHealthMetricAdded = (metricName: any) => {
    const found = inputData?.healthMetrics?.find(
      (item: any) => item?.metricName === metricName
    );
    return found;
  };

  return (
    <div className="container-lg">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb mb-3">
          <li className="breadcrumb-item">
            <a href="/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/users/${userId}/reports`} state={subscription}>
              Reports
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Report Details
          </li>
        </ol>
      </nav>
      {userDetails?.fullName && (
        <h6 className="fw-700 fs-24 mb-2">
          Edit {userDetails?.fullName}'s report
        </h6>
      )}
      <div className="row">
        <div className="col-6">
          <form onSubmit={handleSubmit}>
            <div className="form-group mb-3">
              <label htmlFor="generation-date" className="form-label">
                <strong>Report time</strong>
              </label>
              <input
                className="form-control"
                type="datetime-local"
                id="generation-date"
                name="reportGeneratedAt"
                onChange={handleChange}
                value={moment(+inputData.reportGeneratedAt).format(
                  "YYYY-MM-DDThh:mm"
                )}
                disabled={!isEditReportAllowed}
              />
            </div>
            <div className="form-group mb-3">
              <label htmlFor="issue-type" className="form-label">
                <strong>Health issue type</strong>
              </label>
              <select
                id="issue-type"
                className="form-select"
                aria-label="Default select example"
                name="healthIssueType"
                defaultValue=""
                onChange={handleChange}
                value={inputData.healthIssueType}
                disabled={!isEditReportAllowed}
              >
                <option value="">Select issue type</option>
                {Object.keys(issueTypeMap)?.map((item: any, index: number) => {
                  const issueKey = item as keyof typeof issueTypeMap;
                  return (
                    <option value={issueKey} key={`issue type option ${index}`}>
                      {issueTypeMap[issueKey]?.displayName}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="form-group mb-3">
              <label htmlFor="document-type" className="form-label">
                <strong>File type</strong>
              </label>
              <select
                id="document-type"
                className="form-select"
                aria-label="Default select example"
                defaultValue=""
                name="documentType"
                onChange={handleChange}
                value={inputData?.documentType}
                disabled={!isEditReportAllowed}
              >
                <option value="">Select file type</option>
                {fileTypeOption?.map((item: any, index: number) => {
                  return (
                    <option
                      value={item?.value}
                      key={`issue type option ${index}`}
                    >
                      {item?.displayName}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="form-group mb-3">
              <label htmlFor="document-type" className="form-label">
                <strong>Health Metrics</strong>
              </label>
              {isEditReportAllowed && (
                <div className="mb-4">
                  <div className="d-flex align-items-center">
                    <select
                      id="document-type"
                      className="form-select me-2"
                      aria-label="Default select example"
                      defaultValue=""
                      name="healthMetricKey"
                      onChange={handleChange}
                      value={healthMetric?.metricName}
                      disabled={!isEditReportAllowed}
                    >
                      <option value="">Select metric</option>
                      {healthMetrics?.map((item: any, index: number) => {
                        return (
                          !isHealthMetricAdded(item?.value) && (
                            <option
                              value={item?.value}
                              key={`issue type option ${index}`}
                            >
                              {item?.displayName}
                            </option>
                          )
                        );
                      })}
                    </select>
                    <input
                      className="form-control"
                      placeholder="Metric value"
                      value={healthMetric?.metricValue}
                      name="healthMetricValue"
                      onChange={handleChange}
                      disabled={!isEditReportAllowed}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mt-2"
                    onClick={handleAddMetric}
                  >
                    Add Metric
                  </button>
                </div>
              )}

              <div className="d-flex flex-column-reverse">
                {inputData?.healthMetrics?.map((item: any, index: number) => (
                  <div
                    className="d-flex align-items-center justify-content-between mb-2"
                    key={`health-metric-${index}`}
                  >
                    <span>
                      <strong>{healthMetricsMap[item?.metricName]} : </strong>
                      {item?.metricValue}
                    </span>
                    {isEditReportAllowed && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm rounded-circle"
                        onClick={() => {
                          handleMetricDelete(item?.metricName);
                        }}
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="form-group mb-3">
              <label htmlFor="document-type" className="form-label">
                <strong>Notes</strong>
              </label>
              {isEditReportAllowed && (
                <div className="">
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Add notes for report"
                    value={note?.text || ""}
                    name="note"
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mt-2"
                    onClick={handleAddNote}
                  >
                    Add note
                  </button>
                </div>
              )}

              <div className="d-flex flex-column-reverse">
                {inputData?.notes?.map((item: any, index: number) => (
                  <div className="card p-2 mt-3" key={`notes-${index}`}>
                    <div className="bold text-sm d-flex align-items-center justify-content-between">
                      <strong>{item?.trainerDetails?.trainerName}</strong>
                      <small>
                        {moment(item?.createdAt).format("DD MMM YYYY, hh:mm A")}
                      </small>
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: item?.text }} />
                  </div>
                ))}
              </div>
            </div>

            {isEditReportAllowed && (
              <div className="form-group mb-3">
                <button type="submit" className="btn btn-outline-success px-4">
                  Update report
                </button>
              </div>
            )}
          </form>
        </div>
        <div className="col-6 px-5">
          {inputData?.fileDetails?.fileContentType?.includes("image") && (
            <>
              <Link
                to={inputData?.fileDetails?.s3URL}
                target="_blank"
                className="mb-2 d-block"
              >
                View image
              </Link>
              <img
                src={inputData?.fileDetails?.s3URL}
                className="w-100 mx-auto d-block"
                height={"550px"}
              />
            </>
          )}
          {inputData?.fileDetails?.fileContentType?.includes("pdf") && (
            <>
              <Link
                to={inputData?.fileDetails?.s3URL}
                target="_blank"
                className="mb-2 d-block"
              >
                View file
              </Link>
              <iframe
                src={inputData?.fileDetails?.s3URL}
                width="100%"
                height="550px"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateReportPage;
